import React, { useEffect, useMemo, useState, createElement, Component } from 'react';
import { Download, Package, XCircle, Check, Loader2, Plus, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, MoreVertical, Clock, Building2, User, CheckSquare, Square, MinusSquare } from 'lucide-react';
import { MetricsBar } from '../components/MetricsBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { SearchBar } from '../components/ui/SearchBar';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { filterTodayOrders } from '../utils/dateFilters';
import { cleanProductName, displayProductName } from '../utils/formatters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { MenuItemOptionsModal } from '../components/MenuItemOptionsModal';
import { useHasPermission } from '../utils/permissions';
import { exportOrdersToExcel } from '../utils/excelExport';
// Helper: Split combined product strings into individual products
// Handles: "Arroz blacon, Plato del dia" → ["Arroz blacon", "Plato del dia"]
// Respects commas inside parentheses: "Plato del dia (Pollo, Arroz)" stays as one item
function splitCombinedProducts(productStr: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  for (const char of productStr) {
    if (char === '(') depth++;else if (char === ')') depth--;else if (char === ',' && depth === 0) {
      if (current.trim()) parts.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}
// Helper: Expand a single combined order into multiple display rows
function expandCombinedOrders(orders: any[]): any[] {
  return orders.flatMap(order => {
    const products = splitCombinedProducts(order.product);
    // Only split if there are multiple products AND quantity matches product count
    // This handles legacy combined orders like "Arroz blacon, Plato del dia" with qty 2
    if (products.length > 1 && order.quantity >= products.length) {
      const perItemTotal = parseFloat(order.total) / products.length;
      const perItemSubtotal = parseFloat(order.subtotal || order.total) / products.length;
      const perItemSubsidy = parseFloat(order.subsidyAmount || '0') / products.length;
      const perItemDiscount = parseFloat(order.discount || '0') / products.length;
      const qtyPerItem = Math.floor(order.quantity / products.length);
      return products.map((prod: string, idx: number) => ({
        ...order,
        id: `${order.id}_split_${idx}`,
        _originalId: order.id,
        _originalOrderNo: order.orderNo,
        product: prod,
        quantity: qtyPerItem,
        total: perItemTotal.toFixed(2),
        subtotal: perItemSubtotal.toFixed(2),
        subsidyAmount: perItemSubsidy.toFixed(2),
        discount: perItemDiscount.toFixed(2)
      }));
    }
    return [{
      ...order,
      _originalId: order.id
    }];
  });
}
export function OrdersDashboard() {
  useDocumentTitle('Órdenes');
  // Permission checks
  const canCreate = useHasPermission('orders.create');
  const canEdit = useHasPermission('orders.edit');
  const canDelete = useHasPermission('orders.delete');
  const canExport = useHasPermission('orders.export');
  const {
    filteredOrders,
    orders,
    loading,
    cancelOrder,
    updateOrderStatus,
    addOrder,
    refreshOrders // <--- IMPORTANTE: Obtenemos la función de recarga
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('Todas');
  const [branchFilter, setBranchFilter] = useState('Todas');
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>('today');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false); // Estado visual de carga
  // Ordenamiento
  const [sortColumn, setSortColumn] = useState<string>('orderNo');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Selección masiva
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const companies = getCompanies();
  const companyNames = ['Todas', ...companies.map(c => c.name)];
  // --- LISTENER: Mini-notificaciones visuales cuando llega una nueva orden ---
  useEffect(() => {
    const handleNewOrder = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        showToast('info', `Nueva Orden #${detail.orderNo}`, `${detail.name} — ${detail.product} (${detail.company})`);
      }
    };
    window.addEventListener('cricket-new-order', handleNewOrder);
    return () => window.removeEventListener('cricket-new-order', handleNewOrder);
  }, [showToast]);
  // --- EFECTO DE ACTUALIZACIÓN AUTOMÁTICA (POLLING) ---
  // REMOVED: The OrderContext already has real-time subscription + fallback polling.
  // Having a SECOND 5s polling here caused massive lag (3-4 simultaneous DB queries).
  // The isAutoRefreshing indicator is now driven by the real-time subscription below.
  // Sincronización en tiempo real (Supabase Subscription) — lightweight visual indicator only
  // The actual data refresh is handled by OrderContext's own subscription
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const subscription = supabaseClient.subscribeToOrders(() => {
      // Just flash the refresh indicator — OrderContext handles the actual data reload
      setIsAutoRefreshing(true);
      setTimeout(() => setIsAutoRefreshing(false), 800);
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // Load menu items and employees for order creation
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isSupabaseConfigured()) {
          const [items, emps] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getEmployees()]);
          setMenuItems(items || []);
          setEmployees(emps || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);
  // Get branches for selected company
  const availableBranches = useMemo(() => {
    if (companyFilter === 'Todas') {
      return ['Todas'];
    }
    const company = companies.find(c => c.name === companyFilter);
    const branchNames = new Set<string>();
    // Get branches from company data
    if (company?.branches) {
      company.branches.forEach((b: any) => {
        if (b.name) branchNames.add(b.name);
      });
    }
    // Also include branches from orders as fallback
    orders.filter(o => o.company === companyFilter).forEach(o => {
      if (o.branch) branchNames.add(o.branch);
    });
    return ['Todas', ...Array.from(branchNames).sort()];
  }, [companyFilter, companies, orders]);
  // Reset branch filter when company changes
  useEffect(() => {
    setBranchFilter('Todas');
  }, [companyFilter]);
  // Filter orders by date, company and branch
  const displayOrders = useMemo(() => {
    let filtered = filteredOrders(searchTerm, companyFilter);
    // Filtrar por fecha (solo órdenes de hoy)
    if (dateFilter === 'today') {
      filtered = filterTodayOrders(filtered);
    }
    // Apply branch filter
    if (branchFilter !== 'Todas') {
      filtered = filtered.filter(order => order.branch === branchFilter);
    }
    // EXPAND combined orders into individual display rows
    // This handles legacy orders like "Arroz blacon, Plato del dia" with qty 2
    const expanded = expandCombinedOrders(filtered);
    // Aplicar ordenamiento
    const sorted = [...expanded].sort((a, b) => {
      let valA: any;
      let valB: any;
      switch (sortColumn) {
        case 'orderNo':
          valA = a.orderNo || '';
          valB = b.orderNo || '';
          break;
        case 'orderDate':
          valA = a.orderDateISO || a.order_date || a.created_at || a.orderDate || '';
          valB = b.orderDateISO || b.order_date || b.created_at || b.orderDate || '';
          break;
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'company':
          valA = (a.company || '').toLowerCase();
          valB = (b.company || '').toLowerCase();
          break;
        case 'product':
          valA = (a.product || '').toLowerCase();
          valB = (b.product || '').toLowerCase();
          break;
        case 'quantity':
          valA = a.quantity || 0;
          valB = b.quantity || 0;
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'subsidyAmount':
          valA = parseFloat(a.subsidyAmount || '0');
          valB = parseFloat(b.subsidyAmount || '0');
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'total':
          valA = parseFloat(a.total || '0');
          valB = parseFloat(b.total || '0');
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        case 'labelStatus':
          valA = (a.labelStatus || '').toLowerCase();
          valB = (b.labelStatus || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredOrders, searchTerm, companyFilter, branchFilter, dateFilter, sortColumn, sortDirection]);
  // Paginación
  const totalPages = Math.ceil(displayOrders.length / itemsPerPage);
  const paginatedOrders = displayOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter, branchFilter, dateFilter, sortColumn, sortDirection]);
  // Calculate totals from DISPLAYED orders only (respecting date filter)
  const activeDisplayOrders = displayOrders.filter(o => o.status !== 'Cancelado');
  const totalSubtotal = activeDisplayOrders.reduce((sum, order) => {
    const total = parseFloat(order.total);
    return sum + (order.taxExempt ? total : total / 1.18);
  }, 0);
  const totalTax = activeDisplayOrders.reduce((sum, order) => {
    const total = parseFloat(order.total);
    return sum + (order.taxExempt ? 0 : total - total / 1.18);
  }, 0);
  const totalAmount = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.total), 0);
  const totalDiscount = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.discount || '0'), 0);
  const totalSubsidy = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.subsidyAmount || '0'), 0);
  const totalCount = activeDisplayOrders.length;
  const metrics = [{
    label: 'Cantidad',
    value: totalCount.toString()
  }, {
    label: 'Subtotal',
    value: `RD$ ${totalSubtotal.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'ITBIS (18%)',
    value: `RD$ ${totalTax.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Descuento',
    value: `RD$ ${totalDiscount.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Subsidios',
    value: `RD$ ${totalSubsidy.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Total Facturado',
    value: `RD$ ${totalAmount.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }];
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  // --- Bulk Selection Helpers ---
  // Only orders that can have their status changed (not Cancelado/Entregado)
  const selectableOrders = paginatedOrders.filter(o => o.status !== 'Cancelado' && o.status !== 'Entregado');
  const toggleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedOrders.size === selectableOrders.length && selectableOrders.length > 0) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(selectableOrders.map(o => o._originalId || o.id)));
    }
  };
  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedOrders.size === 0) return;
    setBulkProcessing(true);
    // Deduplicate by original ID (split orders share the same original)
    const uniqueIds = new Set<string>();
    selectedOrders.forEach(id => {
      // Find the order to get its _originalId
      const order = displayOrders.find(o => o.id === id || o._originalId === id);
      uniqueIds.add(order?._originalId || id);
    });
    let successCount = 0;
    let errorCount = 0;
    for (const orderId of uniqueIds) {
      try {
        if (newStatus === 'Cancelado') {
          await cancelOrder(orderId);
        } else {
          await updateOrderStatus(orderId, newStatus as any);
        }
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    if (successCount > 0) {
      addLog('Cambio Masivo de Estado', `${successCount} órdenes cambiadas a "${newStatus}"`, 'order');
      showToast('success', 'Estado actualizado', `${successCount} orden(es) cambiada(s) a "${newStatus}"${errorCount > 0 ? ` (${errorCount} error(es))` : ''}`);
    } else {
      showToast('error', 'Error', 'No se pudieron actualizar las órdenes');
    }
    setSelectedOrders(new Set());
    setShowBulkStatusModal(false);
    setBulkProcessing(false);
  };
  // Clear selection when filters change
  useEffect(() => {
    setSelectedOrders(new Set());
  }, [searchTerm, companyFilter, branchFilter, dateFilter, currentPage]);
  const SortIcon = ({
    column
  }: {
    column: string;
  }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={12} className="text-gray-300 dark:text-gray-600 ml-1 inline-block" data-id="element-4776" />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={12} className="text-blue-600 dark:text-blue-400 ml-1 inline-block" data-id="element-4777" /> : <ArrowDown size={12} className="text-blue-600 dark:text-blue-400 ml-1 inline-block" data-id="element-4778" />;
  };
  const handleCancelOrder = (order: any) => {
    // Use original order ID for actions on split rows
    const originalOrder = order._originalId ? {
      ...order,
      id: order._originalId
    } : order;
    setSelectedOrder(originalOrder);
    setShowCancelModal(true);
  };
  const handleChangeStatus = (order: any) => {
    // Use original order ID for actions on split rows
    const originalOrder = order._originalId ? {
      ...order,
      id: order._originalId
    } : order;
    setSelectedOrder(originalOrder);
    setShowStatusModal(true);
  };
  const confirmCancel = async () => {
    if (selectedOrder) {
      try {
        await cancelOrder(selectedOrder.id);
        addLog('Pedido Anulado', `Orden #${selectedOrder.orderNo} - ${selectedOrder.name} - ${selectedOrder.company}`, 'order');
        showToast('success', 'Pedido anulado', `Orden #${selectedOrder.orderNo} ha sido anulada`);
        setShowCancelModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo anular el pedido');
      }
    }
  };
  const confirmStatusChange = async (newStatus: any) => {
    if (selectedOrder) {
      try {
        await updateOrderStatus(selectedOrder.id, newStatus);
        addLog('Estado de Pedido Actualizado', `Orden #${selectedOrder.orderNo} cambió a ${newStatus}`, 'order');
        showToast('success', 'Estado actualizado', `Orden #${selectedOrder.orderNo} ahora está ${newStatus}`);
        setShowStatusModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo actualizar el estado');
      }
    }
  };
  const exportOrders = () => {
    const ordersToExport = displayOrders;
    if (ordersToExport.length === 0) {
      showToast('warning', 'Sin datos', 'No hay órdenes para exportar');
      return;
    }
    exportOrdersToExcel({
      filters: {
        company: companyFilter,
        branch: branchFilter,
        dateFilter: dateFilter === 'today' ? 'Solo hoy' : 'Todas'
      },
      orders: ordersToExport.map(order => {
        const orderTotal = parseFloat(order.total);
        const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
        const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
        const orderSubsidy = parseFloat(order.subsidyAmount || '0');
        return {
          orderNo: order.orderNo,
          orderDate: order.orderDate,
          name: order.name,
          company: order.company,
          branch: order.branch || '-',
          product: order.product,
          quantity: order.quantity,
          subtotal: orderSubtotal,
          tax: orderTax,
          discount: parseFloat(order.discount || '0'),
          subsidy: orderSubsidy,
          total: orderTotal,
          status: order.status,
          labelStatus: order.labelStatus,
          taxExempt: order.taxExempt || false
        };
      })
    });
    showToast('success', 'Exportación exitosa', `${ordersToExport.length} órdenes exportadas a Excel`);
  };
  // Loading state
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-4779">
        <div className="flex items-center justify-center h-96" data-id="element-4780">
          <div className="text-center" data-id="element-4781">
            <Loader2 className="w-12 h-12 animate-spin text-gray-400 mx-auto mb-4" data-id="element-4782" />
            <p className="text-gray-600 dark:text-gray-400" data-id="element-4783">
              Cargando pedidos...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-4784">
      <MetricsBar metrics={metrics} data-id="element-4785" />

      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-4786">
        <div className="flex flex-col gap-4 mb-6" data-id="element-4787">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-4788">
            <div data-id="element-4789">
              <div className="flex items-center gap-2" data-id="element-4790">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4791">
                  Órdenes {dateFilter === 'today' ? 'de Hoy' : ''}
                </h2>
                {/* Indicador de actualización automática */}
                {isAutoRefreshing && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" data-id="element-4792" />}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-4793">
                {displayOrders.length} órdenes encontradas
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0" data-id="element-4794">
              {canCreate && <Button variant="outline" leftIcon={<Plus className="w-4 h-4" data-id="element-4796" />} onClick={() => setShowCreateOrderModal(true)} data-id="element-4795">
                  Nueva Orden
                </Button>}

              {canExport && <Button variant="primary" leftIcon={<Download className="w-4 h-4" data-id="element-4798" />} onClick={exportOrders} data-id="element-4797">
                  Exportar
                </Button>}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3" data-id="element-4799">
            <FilterDropdown value={dateFilter === 'today' ? 'Solo hoy' : 'Todas las fechas'} options={['Solo hoy', 'Todas las fechas']} onChange={val => setDateFilter(val === 'Solo hoy' ? 'today' : 'all')} className="w-full xs:w-auto min-w-[140px]" data-id="element-4800" />

            <FilterDropdown label="Empresa" value={companyFilter} options={companyNames} onChange={setCompanyFilter} className="w-full xs:w-auto min-w-[140px]" data-id="element-4801" />

            {companyFilter !== 'Todas' && <FilterDropdown value={branchFilter} options={availableBranches} onChange={setBranchFilter} className="w-full xs:w-auto min-w-[140px]" data-id="element-4802" />}

            <SearchBar className="w-full sm:w-64" onSearch={setSearchTerm} placeholder="Buscar orden..." data-id="element-4803" />
          </div>
        </div>

        {orders.length === 0 ? <div className="text-center py-20 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-4804">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-4805" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4806">
              No hay órdenes registradas
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-4807">
              Las órdenes aparecerán aquí cuando los empleados hagan pedidos.
            </p>
          </div> : displayOrders.length === 0 ? <div className="text-center py-20 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-4808">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-4809" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4810">
              {dateFilter === 'today' ? 'No hay pedidos para hoy' : 'No se encontraron órdenes'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-4811">
              {dateFilter === 'today' ? 'Los pedidos de hoy aparecerán aquí cuando se registren.' : 'Intenta ajustar los filtros de búsqueda'}
            </p>
          </div> : <>
            {/* Desktop Table View - only on xl screens */}
            <div className="hidden xl:block overflow-x-auto" data-id="element-4812">
              <table className="w-full text-left border-collapse min-w-[1200px]" data-id="element-4813">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-4814">
                  <tr data-id="element-4815">
                    <th className="px-2 py-3 w-10" data-id="element-4816">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" title={selectedOrders.size === selectableOrders.length && selectableOrders.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'} data-id="element-4817">
                        {selectedOrders.size === 0 ? <Square size={18} data-id="element-4818" /> : selectedOrders.size === selectableOrders.length ? <CheckSquare size={18} className="text-blue-600" data-id="element-4819" /> : <MinusSquare size={18} className="text-blue-600" data-id="element-4820" />}
                      </button>
                    </th>
                    <th onClick={() => handleSort('orderNo')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[70px]" data-id="element-4821">
                      No. Orden <SortIcon column="orderNo" data-id="element-4822" />
                    </th>
                    <th onClick={() => handleSort('orderDate')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[140px]" data-id="element-4823">
                      Fecha/Hora <SortIcon column="orderDate" data-id="element-4824" />
                    </th>
                    <th onClick={() => handleSort('name')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-4825">
                      Cliente <SortIcon column="name" data-id="element-4826" />
                    </th>
                    <th onClick={() => handleSort('company')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-4827">
                      Empresa <SortIcon column="company" data-id="element-4828" />
                    </th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap" data-id="element-4829">
                      Sucursal
                    </th>
                    <th onClick={() => handleSort('product')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-4830">
                      Producto <SortIcon column="product" data-id="element-4831" />
                    </th>
                    <th onClick={() => handleSort('quantity')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[55px] text-center" data-id="element-4832">
                      Cant. <SortIcon column="quantity" data-id="element-4833" />
                    </th>
                    <th onClick={() => handleSort('total')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[70px]" data-id="element-4834">
                      ITBIS <SortIcon column="total" data-id="element-4835" />
                    </th>
                    <th onClick={() => handleSort('total')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[90px]" data-id="element-4836">
                      Total <SortIcon column="total" data-id="element-4837" />
                    </th>
                    <th onClick={() => handleSort('status')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap w-[110px]" data-id="element-4838">
                      Estado <SortIcon column="status" data-id="element-4839" />
                    </th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right whitespace-nowrap w-[80px]" data-id="element-4840">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" data-id="element-4841">
                  {paginatedOrders.map(order => <tr key={order.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedOrders.has(order._originalId || order.id) ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`} data-id="element-4842">
                      <td className="px-2 py-3" data-id="element-4843">
                        {order.status !== 'Cancelado' && order.status !== 'Entregado' ? <button onClick={e => {
                    e.stopPropagation();
                    toggleSelectOrder(order._originalId || order.id);
                  }} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" data-id="element-4844">
                            {selectedOrders.has(order._originalId || order.id) ? <CheckSquare size={18} className="text-blue-600" data-id="element-4845" /> : <Square size={18} data-id="element-4846" />}
                          </button> : <div className="w-[18px]" data-id="element-4847" />}
                      </td>
                      <td className="px-3 py-3 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap" data-id="element-4848">
                        {order.orderNo}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap" data-id="element-4849">
                        {order.orderDate}
                      </td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4850">
                        <div className="truncate max-w-[160px]" title={order.name} data-id="element-4851">
                          {order.name}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-4852">
                        <div className="truncate max-w-[180px]" title={order.company} data-id="element-4853">
                          {order.company}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-4854">
                        <div className="truncate max-w-[130px]" title={order.branch || '-'} data-id="element-4855">
                          {order.branch || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm" data-id="element-4856">
                        <div className="line-clamp-2 leading-snug" title={cleanProductName(order.product)} data-id="element-4857">
                          {displayProductName(order.product)}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-gray-900 dark:text-white whitespace-nowrap" data-id="element-4858">
                        {order.quantity}
                      </td>
                      <td className="px-3 py-3 text-sm whitespace-nowrap" data-id="element-4859">
                        {order.taxExempt ? <span className="text-gray-400 dark:text-gray-500" data-id="element-4860">
                            Exento
                          </span> : <span className="text-gray-700 dark:text-gray-300" data-id="element-4861">
                            RD${' '}
                            {(parseFloat(order.total) - parseFloat(order.total) / 1.18).toFixed(2)}
                          </span>}
                      </td>
                      <td className="px-3 py-3 text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap" data-id="element-4862">
                        RD$ {parseFloat(order.total).toFixed(2)}
                      </td>
                      <td className="px-3 py-3" data-id="element-4863">
                        <button onClick={() => handleChangeStatus(order)} disabled={order.status === 'Cancelado' || !canEdit} className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${order.status === 'Pendiente' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : order.status === 'En Preparación' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : order.status === 'Listo' ? 'bg-green-100 text-green-800 hover:bg-green-200' : order.status === 'Entregado' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'} ${order.status !== 'Cancelado' && order.status !== 'Entregado' && canEdit ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-4864">
                          {order.status}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap" data-id="element-4865">
                        {order.status !== 'Cancelado' && order.status !== 'Entregado' && canDelete && <button onClick={() => handleCancelOrder(order)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium" data-id="element-4866">
                              Anular
                            </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile & Tablet Card View */}
            <div className="xl:hidden space-y-3" data-id="element-4867">
              {paginatedOrders.map(order => <div key={order.id} className={`bg-white dark:bg-gray-900 p-4 rounded-xl border shadow-sm ${selectedOrders.has(order._originalId || order.id) ? 'border-blue-400 bg-blue-50/30 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-800'}`} data-id="element-4868">
                  <div className="flex justify-between items-start mb-3" data-id="element-4869">
                    <div className="flex items-center gap-3" data-id="element-4870">
                      {order.status !== 'Cancelado' && order.status !== 'Entregado' && <button onClick={() => toggleSelectOrder(order._originalId || order.id)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0" data-id="element-4871">
                            {selectedOrders.has(order._originalId || order.id) ? <CheckSquare size={20} className="text-blue-600" data-id="element-4872" /> : <Square size={20} data-id="element-4873" />}
                          </button>}
                      <div data-id="element-4874">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-4875">
                          <span className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400" data-id="element-4876">
                            #{order.orderNo}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-4877">
                            {order.orderDate}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 dark:text-white mt-1" data-id="element-4878">
                          {order.name}
                        </h3>
                      </div>
                    </div>
                    <button onClick={() => handleChangeStatus(order)} disabled={order.status === 'Cancelado' || !canEdit} className={`px-2 py-1 text-xs font-medium rounded-full shrink-0 ${order.status === 'Pendiente' ? 'bg-yellow-100 text-yellow-800' : order.status === 'En Preparación' ? 'bg-blue-100 text-blue-800' : order.status === 'Listo' ? 'bg-green-100 text-green-800' : order.status === 'Entregado' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`} data-id="element-4879">
                      {order.status}
                    </button>
                  </div>

                  <div className="space-y-2 mb-3" data-id="element-4880">
                    <div className="flex items-start gap-2 text-sm" data-id="element-4881">
                      <Building2 size={16} className="text-gray-400 mt-0.5 shrink-0" data-id="element-4882" />
                      <span className="text-gray-600 dark:text-gray-300" data-id="element-4883">
                        {order.company}
                        {order.branch ? ` — ${order.branch}` : ''}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 text-sm" data-id="element-4884">
                      <Package size={16} className="text-gray-400 mt-0.5 shrink-0" data-id="element-4885" />
                      <div className="flex-1" data-id="element-4886">
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-4887">
                          {displayProductName(order.product)}
                        </span>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5" data-id="element-4888">
                          <span data-id="element-4889">Cant: {order.quantity}</span>
                          <span data-id="element-4890">
                            {order.taxExempt ? 'Exento ITBIS' : `ITBIS: RD$ ${(parseFloat(order.total) - parseFloat(order.total) / 1.18).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-4891">
                    <div className="font-bold text-gray-900 dark:text-white" data-id="element-4892">
                      RD$ {parseFloat(order.total).toFixed(2)}
                    </div>
                    {order.status !== 'Cancelado' && order.status !== 'Entregado' && canDelete && <button onClick={() => handleCancelOrder(order)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium flex items-center gap-1" data-id="element-4893">
                          <XCircle size={14} data-id="element-4894" /> Anular
                        </button>}
                  </div>
                </div>)}
            </div>

            {/* Paginación */}
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={displayOrders.length} itemsPerPage={itemsPerPage} data-id="element-4895" />}
          </>}
      </div>

      {/* Floating Bulk Action Bar */}
      {selectedOrders.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white px-6 py-3 rounded-2xl shadow-2xl border border-gray-700 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-300" data-id="element-4896">
          <span className="text-sm font-medium whitespace-nowrap" data-id="element-4897">
            {selectedOrders.size} orden(es) seleccionada(s)
          </span>
          <div className="h-5 w-px bg-gray-600" data-id="element-4898" />
          <button onClick={() => setShowBulkStatusModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors whitespace-nowrap" data-id="element-4899">
            <Check size={14} data-id="element-4900" />
            Cambiar Estado
          </button>
          <button onClick={() => handleBulkStatusChange('Cancelado')} disabled={bulkProcessing} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50" data-id="element-4901">
            <XCircle size={14} data-id="element-4902" />
            Anular
          </button>
          <button onClick={() => setSelectedOrders(new Set())} className="text-gray-400 hover:text-white text-sm transition-colors whitespace-nowrap" data-id="element-4903">
            Cancelar
          </button>
        </div>}

      {/* Bulk Status Change Modal */}
      {showBulkStatusModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-4904">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4905">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-4906">
              <div className="flex items-center gap-3" data-id="element-4907">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg" data-id="element-4908">
                  <CheckSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-4909" />
                </div>
                <div data-id="element-4910">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4911">
                    Cambio Masivo de Estado
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4912">
                    {selectedOrders.size} orden(es) seleccionada(s)
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-4913">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-4914">
                Selecciona el nuevo estado para todas las órdenes seleccionadas:
              </p>
              <div className="space-y-2" data-id="element-4915">
                {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map(status => <button key={status} onClick={() => handleBulkStatusChange(status)} disabled={bulkProcessing} className="w-full p-3 rounded-lg text-left font-medium transition-colors bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between" data-id="element-4916">
                      <span data-id="element-4917">{status}</span>
                      {bulkProcessing && <Loader2 size={16} className="animate-spin text-gray-400" data-id="element-4918" />}
                    </button>)}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-4919">
              <Button variant="outline" onClick={() => setShowBulkStatusModal(false)} fullWidth disabled={bulkProcessing} data-id="element-4920">
                Cancelar
              </Button>
            </div>
          </div>
        </div>}

      {/* Create Order Modal */}
      {showCreateOrderModal && <CreateOrderModal companies={companies} employees={employees} menuItems={menuItems} onClose={() => setShowCreateOrderModal(false)} onSave={(orderData: any) => {
      try {
        addOrder(orderData);
        addLog('Orden Creada por Admin', `${orderData.name} - ${orderData.company}`, 'order');
        showToast('success', 'Orden creada', 'La orden ha sido creada exitosamente');
        setShowCreateOrderModal(false);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo crear la orden');
      }
    }} data-id="element-4921" />}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-4922">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4923">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-4924">
              <div className="flex items-center gap-3" data-id="element-4925">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg" data-id="element-4926">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-4927" />
                </div>
                <div data-id="element-4928">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4929">
                    Cambiar Estado
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4930">
                    Orden #{selectedOrder.orderNo}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-4931">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-4932">
                Selecciona el nuevo estado del pedido:
              </p>
              <div className="space-y-2" data-id="element-4933">
                {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map(status => <button key={status} onClick={() => confirmStatusChange(status)} disabled={selectedOrder.status === status} className={`w-full p-3 rounded-lg text-left font-medium transition-colors ${selectedOrder.status === status ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`} data-id="element-4934">
                      {status}
                    </button>)}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-4935">
              <Button variant="outline" onClick={() => setShowStatusModal(false)} fullWidth data-id="element-4936">
                Cancelar
              </Button>
            </div>
          </div>
        </div>}

      {/* Cancel Order Modal */}
      {showCancelModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-4937">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4938">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-4939">
              <div className="flex items-center gap-3" data-id="element-4940">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg" data-id="element-4941">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-id="element-4942" />
                </div>
                <div data-id="element-4943">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4944">
                    Anular Pedido
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4945">
                    Orden #{selectedOrder.orderNo}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-4946">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-4947">
                ¿Estás seguro que deseas anular este pedido? Esta acción no se
                puede deshacer.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-4948">
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4949">
                  <strong data-id="element-4950">Cliente:</strong> {selectedOrder.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4951">
                  <strong data-id="element-4952">Producto:</strong> {selectedOrder.product}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4953">
                  <strong data-id="element-4954">Total:</strong> RD$ {selectedOrder.total}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-4955">
              <Button variant="outline" onClick={() => setShowCancelModal(false)} fullWidth data-id="element-4956">
                Cancelar
              </Button>
              <Button onClick={confirmCancel} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-4957">
                Anular Pedido
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}
// Create Order Modal Component
function CreateOrderModal({
  companies,
  employees,
  menuItems,
  onClose,
  onSave
}: any) {
  const [formData, setFormData] = useState({
    employeeId: '',
    companyId: 0,
    branchId: '',
    menuItemId: '',
    quantity: 1,
    type: 'Envío'
  });
  // Custom date — defaults to today in YYYY-MM-DD
  const todayStr = new Date().toISOString().split('T')[0];
  const [customDate, setCustomDate] = useState(todayStr);
  // Toggle: menu selection vs manual entry
  const [manualMode, setManualMode] = useState(false);
  const [customProduct, setCustomProduct] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  // Employee selection state
  const [employeeCompanyFilter, setEmployeeCompanyFilter] = useState('Todas');
  const [employeeSearch, setEmployeeSearch] = useState('');
  // Menu item options state
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [optionsPrice, setOptionsPrice] = useState(0);
  // Filter only available menu items
  const availableMenuItems = menuItems.filter((item: any) => item.available !== false);
  const selectedCompany = companies.find((c: any) => c.id === formData.companyId);
  const selectedEmployee = employees.find((e: any) => e.id === formData.employeeId);
  const selectedMenuItem = availableMenuItems.find((m: any) => m.id === formData.menuItemId);
  const selectedBranch = selectedCompany?.branches.find((b: any) => b.id === formData.branchId);
  // Filtered employees by company + search
  const filteredEmployees = employees.filter((emp: any) => {
    const companyName = companies.find((c: any) => c.id === emp.company_id)?.name || '';
    const matchesCompany = employeeCompanyFilter === 'Todas' || companyName === employeeCompanyFilter;
    const searchLower = employeeSearch.toLowerCase();
    const matchesSearch = !employeeSearch || emp.name?.toLowerCase().includes(searchLower) || companyName.toLowerCase().includes(searchLower) || emp.username?.toLowerCase().includes(searchLower);
    return matchesCompany && matchesSearch;
  });
  // Company options for employee filter
  const employeeCompanyOptions = ['Todas', ...companies.map((c: any) => c.name)];
  const menuItemOptions = availableMenuItems.length > 0 ? ['Seleccionar plato', ...availableMenuItems.map((item: any) => `${item.name} - RD$ ${item.price.toFixed(2)}`)] : ['No hay platos disponibles'];
  const menuItemDisplayValue = selectedMenuItem ? `${selectedMenuItem.name} - RD$ ${selectedMenuItem.price.toFixed(2)}` : 'Seleccionar plato';
  // Calculate final price including options (menu mode)
  const itemPrice = selectedMenuItem ? selectedMenuItem.price + optionsPrice : 0;
  const isTaxExempt = selectedCompany?.taxExempt || false;
  const effectiveItemPrice = isTaxExempt ? itemPrice / 1.18 : itemPrice;
  const menuTotalPrice = effectiveItemPrice * formData.quantity;
  // Manual mode total
  const manualTotal = parseFloat(customAmount) || 0;
  // Effective total for display
  const displayTotal = manualMode ? manualTotal : menuTotalPrice;
  const canSubmit = manualMode ? !!(selectedEmployee && customProduct.trim() && manualTotal > 0) : !!(selectedEmployee && selectedMenuItem);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !selectedCompany || !selectedBranch) return;
    // Build custom ISO date from the date picker
    const customOrderDate = new Date(customDate + 'T12:00:00').toISOString();
    if (manualMode) {
      if (!customProduct.trim() || manualTotal <= 0) return;
      const orderData = {
        deliveryDate: customOrderDate,
        customOrderDate,
        type: formData.type,
        name: selectedEmployee.name,
        company: selectedCompany.name,
        branch: selectedBranch.name,
        product: customProduct.trim(),
        cashier: 'Admin',
        quantity: formData.quantity,
        catalogType: 'Almuerzo',
        subtotal: manualTotal.toFixed(2),
        discount: '0',
        total: manualTotal.toFixed(2),
        taxExempt: isTaxExempt,
        employeeId: selectedEmployee.id
      };
      onSave(orderData);
    } else {
      if (!selectedMenuItem) return;
      let productName = selectedMenuItem.name;
      if (selectedOptions.length > 0) {
        const optionNames = selectedOptions.map((o: any) => o.optionName).join(', ');
        productName = `${selectedMenuItem.name} (${optionNames})`;
      }
      const subtotal = effectiveItemPrice * formData.quantity;
      const orderData = {
        deliveryDate: customOrderDate,
        customOrderDate,
        type: formData.type,
        name: selectedEmployee.name,
        company: selectedCompany.name,
        branch: selectedBranch.name,
        product: productName,
        cashier: 'Admin',
        quantity: formData.quantity,
        catalogType: selectedMenuItem.catalogType || 'Almuerzo',
        subtotal: subtotal.toFixed(2),
        discount: '0',
        total: subtotal.toFixed(2),
        taxExempt: isTaxExempt,
        employeeId: selectedEmployee.id
      };
      onSave(orderData);
    }
  };
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const handleOptionsConfirm = (options: any[], totalWithOptions: number) => {
    setSelectedOptions(options);
    setOptionsPrice(totalWithOptions - (selectedMenuItem?.price || 0));
    setShowOptionsModal(false);
  };
  const handleMenuItemChange = (displayValue: string) => {
    const item = availableMenuItems.find((i: any) => `${i.name} - RD$ ${i.price.toFixed(2)}` === displayValue);
    setFormData({
      ...formData,
      menuItemId: item?.id || ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  const handleSelectEmployee = (emp: any) => {
    setFormData({
      ...formData,
      employeeId: emp.id,
      companyId: emp.company_id || 0,
      branchId: emp.branch_id || '',
      menuItemId: ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  const handleClearEmployee = () => {
    setFormData({
      ...formData,
      employeeId: '',
      companyId: 0,
      branchId: '',
      menuItemId: ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  return <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={!showOptionsModal ? onClose : undefined} data-id="element-4958">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" onClick={handleModalClick} data-id="element-4959">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-4960">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4961">
              Crear Nueva Orden
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4962">
              Completa los datos para registrar un pedido
            </p>
          </div>

          <div className="p-6 space-y-5" data-id="element-4963">
            {/* Fecha personalizada */}
            <div data-id="element-4964">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-4965">
                Fecha de la orden
              </label>
              <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-4966" />
            </div>

            {/* Employee Selection — custom searchable picker */}
            <div data-id="element-4967">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-4968">
                Empleado
              </label>

              {selectedEmployee /* Selected employee chip */ ? <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-4969">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-4970">
                    <div className="w-9 h-9 rounded-full bg-gray-900 dark:bg-white flex items-center justify-center text-white dark:text-gray-900 font-bold text-sm shrink-0" data-id="element-4971">
                      {selectedEmployee.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0" data-id="element-4972">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-4973">
                        {selectedEmployee.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4974">
                        {companies.find((c: any) => c.id === selectedEmployee.company_id)?.name || 'Sin empresa'}
                        {selectedBranch?.name ? ` · ${selectedBranch.name}` : ''}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={handleClearEmployee} className="text-xs text-gray-500 hover:text-red-600 dark:hover:text-red-400 font-medium px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0" data-id="element-4975">
                    Cambiar
                  </button>
                </div> /* Employee search + filter + list */ : <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-4976">
                  {/* Filter bar */}
                  <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" data-id="element-4977">
                    <div className="relative flex-1" data-id="element-4978">
                      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4979">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" data-id="element-4980" />
                      </svg>
                      <input type="text" value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} placeholder="Buscar empleado..." className="w-full h-8 pl-8 pr-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-white text-gray-900 dark:text-white placeholder-gray-400" data-id="element-4981" />
                    </div>
                    <select value={employeeCompanyFilter} onChange={e => setEmployeeCompanyFilter(e.target.value)} className="h-8 px-2 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-white max-w-[160px]" data-id="element-4982">
                      {employeeCompanyOptions.map((opt: string) => <option key={opt} value={opt} data-id="element-4983">
                          {opt}
                        </option>)}
                    </select>
                  </div>

                  {/* Employee list */}
                  <div className="max-h-52 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800" data-id="element-4984">
                    {filteredEmployees.length === 0 ? <div className="p-6 text-center" data-id="element-4985">
                        <User className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" data-id="element-4986" />
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4987">
                          {employeeSearch ? 'No se encontraron empleados' : 'No hay empleados disponibles'}
                        </p>
                      </div> : filteredEmployees.map((emp: any) => {
                  const companyName = companies.find((c: any) => c.id === emp.company_id)?.name || 'Sin empresa';
                  const company = companies.find((c: any) => c.id === emp.company_id);
                  const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
                  return <button key={emp.id} type="button" onClick={() => handleSelectEmployee(emp)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-4988">
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-xs shrink-0" data-id="element-4989">
                              {emp.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-4990">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4991">
                                {emp.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4992">
                                {companyName}
                                {branch?.name ? ` · ${branch.name}` : ''}
                              </p>
                            </div>
                            <Building2 className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0" data-id="element-4993" />
                          </button>;
                })}
                  </div>

                  {/* Count footer */}
                  <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700" data-id="element-4994">
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium" data-id="element-4995">
                      {filteredEmployees.length} de {employees.length} empleados
                    </p>
                  </div>
                </div>}
            </div>

            {selectedEmployee && <>
                <div className="grid grid-cols-2 gap-4" data-id="element-4996">
                  <div data-id="element-4997">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-4998">
                      Empresa
                    </label>
                    <div className="w-full h-10 px-3 flex items-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white" data-id="element-4999">
                      {selectedCompany?.name || '—'}
                    </div>
                  </div>
                  <div data-id="element-5000">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-5001">
                      Sucursal
                    </label>
                    <div className="w-full h-10 px-3 flex items-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white" data-id="element-5002">
                      {selectedBranch?.name || '—'}
                    </div>
                  </div>
                </div>

                {/* Toggle: Menú vs Manual */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-5003">
                  <button type="button" onClick={() => {
                setManualMode(false);
                setCustomProduct('');
                setCustomAmount('');
              }} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${!manualMode ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`} data-id="element-5004">
                    Seleccionar del Menú
                  </button>
                  <button type="button" onClick={() => {
                setManualMode(true);
                setFormData(f => ({
                  ...f,
                  menuItemId: ''
                }));
                setSelectedOptions([]);
                setOptionsPrice(0);
              }} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${manualMode ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`} data-id="element-5005">
                    Entrada Manual
                  </button>
                </div>

                {manualMode ? <>
                    {/* Manual: Product name */}
                    <div data-id="element-5006">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-5007">
                        Producto / Descripción
                      </label>
                      <input type="text" value={customProduct} onChange={e => setCustomProduct(e.target.value)} placeholder="Ej. Almuerzo especial, Plato del día..." className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-5008" />
                    </div>

                    {/* Manual: Amount + Quantity */}
                    <div className="grid grid-cols-2 gap-4" data-id="element-5009">
                      <div data-id="element-5010">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-5011">
                          Monto Total (RD$)
                        </label>
                        <div className="relative" data-id="element-5012">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400" data-id="element-5013">
                            RD$
                          </span>
                          <input type="number" step="0.01" min="0" value={customAmount} onChange={e => setCustomAmount(e.target.value)} placeholder="0.00" className="w-full h-10 pl-11 pr-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-mono font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-5014" />
                        </div>
                      </div>
                      <div data-id="element-5015">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-5016">
                          Cantidad
                        </label>
                        <input type="number" required min="1" value={formData.quantity} onChange={e => setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1
                  })} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-5017" />
                      </div>
                    </div>

                    <FilterDropdown label="Tipo" value={formData.type} options={['Envío', 'Retiro']} onChange={val => setFormData({
                ...formData,
                type: val
              })} className="w-full" data-id="element-5018" />

                    {/* Manual summary */}
                    {customProduct && manualTotal > 0 && <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800" data-id="element-5019">
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3" data-id="element-5020">
                          Resumen
                        </h3>
                        <div className="space-y-1.5 text-sm" data-id="element-5021">
                          <div className="flex justify-between" data-id="element-5022">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-5023">
                              Producto:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-5024">
                              {customProduct}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-5025">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-5026">
                              Fecha:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-5027">
                              {new Date(customDate + 'T12:00:00').toLocaleDateString('es-DO', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </span>
                          </div>
                          {formData.quantity > 1 && <div className="flex justify-between" data-id="element-5028">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-5029">
                                Cantidad:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-5030">
                                ×{formData.quantity}
                              </span>
                            </div>}
                          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5031">
                            <span className="font-bold text-gray-900 dark:text-white" data-id="element-5032">
                              Total:
                            </span>
                            <span className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-5033">
                              RD$ {manualTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>}
                  </> : <>
                    {/* Menu mode — existing behavior */}
                    <FilterDropdown label={availableMenuItems.length === 0 ? 'Plato (No hay platos disponibles)' : 'Plato'} value={menuItemDisplayValue} options={menuItemOptions} onChange={val => {
                if (val === 'Seleccionar plato' || val === 'No hay platos disponibles') {
                  setFormData({
                    ...formData,
                    menuItemId: ''
                  });
                  setSelectedOptions([]);
                  setOptionsPrice(0);
                } else {
                  handleMenuItemChange(val);
                }
              }} className="w-full" data-id="element-5034" />

                    {selectedMenuItem && <div data-id="element-5035">
                        <button type="button" onClick={e => {
                  e.stopPropagation();
                  setShowOptionsModal(true);
                }} className="w-full px-4 py-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors flex items-center justify-between" data-id="element-5036">
                          <span data-id="element-5037">
                            {selectedOptions.length > 0 ? `✅ ${selectedOptions.length} opción(es) seleccionada(s)` : '🍽️ Personalizar opciones del plato'}
                          </span>
                          <span className="text-gray-400" data-id="element-5038">→</span>
                        </button>
                        {selectedOptions.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-5039">
                            {selectedOptions.map((opt: any, idx: number) => <span key={idx} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md" data-id="element-5040">
                                {opt.optionName}
                                {opt.priceModifier > 0 && <span className="ml-1 text-gray-500" data-id="element-5041">
                                    +RD$ {opt.priceModifier.toFixed(2)}
                                  </span>}
                              </span>)}
                          </div>}
                      </div>}

                    <div className="grid grid-cols-2 gap-4" data-id="element-5042">
                      <div data-id="element-5043">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-5044">
                          Cantidad
                        </label>
                        <input type="number" required min="1" value={formData.quantity} onChange={e => setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1
                  })} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-5045" />
                      </div>

                      <FilterDropdown label="Tipo" value={formData.type} options={['Envío', 'Retiro']} onChange={val => setFormData({
                  ...formData,
                  type: val
                })} className="w-full" data-id="element-5046" />
                    </div>

                    {selectedMenuItem && <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800" data-id="element-5047">
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3" data-id="element-5048">
                          Resumen
                        </h3>
                        <div className="space-y-1.5 text-sm" data-id="element-5049">
                          <div className="flex justify-between" data-id="element-5050">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-5051">
                              Fecha:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-5052">
                              {new Date(customDate + 'T12:00:00').toLocaleDateString('es-DO', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-5053">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-5054">
                              Precio base:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-5055">
                              RD$ {itemPrice.toFixed(2)}
                            </span>
                          </div>
                          {optionsPrice > 0 && <div className="flex justify-between" data-id="element-5056">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-5057">
                                Opciones:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-5058">
                                +RD$ {optionsPrice.toFixed(2)}
                              </span>
                            </div>}
                          {formData.quantity > 1 && <div className="flex justify-between" data-id="element-5059">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-5060">
                                Cantidad:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-5061">
                                ×{formData.quantity}
                              </span>
                            </div>}
                          <div className="flex justify-between" data-id="element-5062">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-5063">
                              ITBIS (18%):
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-5064">
                              {isTaxExempt ? 'Exento' : `RD$ ${(itemPrice * formData.quantity - itemPrice * formData.quantity / 1.18).toFixed(2)}`}
                            </span>
                          </div>
                          {isTaxExempt && <div className="flex justify-between text-emerald-600 dark:text-emerald-400" data-id="element-5065">
                              <span data-id="element-5066">Descuento ITBIS:</span>
                              <span className="font-medium" data-id="element-5067">
                                - RD${' '}
                                {(itemPrice * formData.quantity - menuTotalPrice).toFixed(2)}
                              </span>
                            </div>}
                          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5068">
                            <span className="font-bold text-gray-900 dark:text-white" data-id="element-5069">
                              Total:
                            </span>
                            <span className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-5070">
                              RD$ {menuTotalPrice.toFixed(2)}
                            </span>
                          </div>
                          {isTaxExempt ? <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-5071">
                              * Empresa exenta de ITBIS — se restó el 18%
                              incluido en el precio
                            </p> : <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" data-id="element-5072">
                              * El ITBIS ya está incluido en el precio del plato
                            </p>}
                        </div>
                      </div>}
                  </>}
              </>}

            <div className="flex gap-3 pt-2" data-id="element-5073">
              <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-5074">
                Cancelar
              </Button>
              <Button type="button" onClick={handleSubmit} fullWidth disabled={!canSubmit} data-id="element-5075">
                Crear Orden
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Item Options Modal - OUTSIDE the backdrop div */}
      {selectedMenuItem && showOptionsModal && <MenuItemOptionsModal menuItem={selectedMenuItem} isOpen={showOptionsModal} onClose={() => setShowOptionsModal(false)} onConfirm={handleOptionsConfirm} data-id="element-5076" />}
    </>;
}